import { createRootRouteWithContext, Outlet, useRouterState } from "@tanstack/react-router";
import { cn } from "@ds/lib/utils";
import { OfflineBanner } from "@/app/components/OfflineBanner";
import { isFullBleedRoute } from "@/app/lib/full-bleed";
import type { AuthStatus } from "@/shared/auth";

export interface RouterContext {
  auth: { status: AuthStatus };
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  /**
   * 🚨 ONE ROUTE GETS NO INSET AT ALL, and it has to be ABSENT rather than cancelled.
   *
   * The timer paints its own full-bleed background and used to pull this padding back with a
   * negative margin. That does nothing: `ScreenTransition`'s stack is `overflow-x-hidden`, and
   * a box with one axis hidden clips the other too — so the ground was laid out above the
   * frame and painted from inside it, leaving the app's near-black background as a bar across
   * the top of the phone and clipping the top of the heart-rate pill with it. The screen
   * supplies the inset again on its own header, which is the same trade it already makes at
   * the bottom with `pb-safe`.
   */
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const fullBleed = isFullBleedRoute(pathname);

  return (
    /**
     * `pt-safe` LIVES HERE, on the frame every screen renders inside, and nowhere else.
     *
     * It used to live on the dev bar — which returns null when the environment is
     * production. So dev and staging cleared the notch and the App Store build rendered its
     * headings under the status bar, on exactly the identity nobody can hot-fix. The native
     * config also disables the web view's own inset, on the stated premise that the web
     * content owns its safe areas; this is the web content owning them.
     */
    <div
      className={cn(
        "flex min-h-dvh flex-col bg-background text-foreground",
        /* THE GROUND CROSSES OVER RATHER THAN CUTTING, because while a strap is live this
           surface IS the heart-rate zone (`HeartRateGround` re-points `--background` at it). A
           zone boundary is crossed mid-effort and often twice in a few seconds; cutting between
           two colours across the whole app is a flicker, where half a second reads as the room
           changing colour. The same duration the timer crossfades its own ground on. */
        "motion-safe:transition-colors motion-safe:duration-[var(--ds-duration-slow)]",
        fullBleed ? undefined : "pt-safe",
      )}
    >
      <OfflineBanner />
      <Outlet />
    </div>
  );
}
